import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useSalesforceSourceSettingsText,
  useSalesforceSourceSettingsViewModel,
} from "@onetasklist/shared/sources/salesforce";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";

interface SalesforceSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const SalesforceSourceSettings: React.FC<
  SalesforceSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset, boardStatuses }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useSalesforceSourceSettingsText(t);
  const vm = useSalesforceSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [objectDefinitionQuery, setObjectDefinitionQuery] = useState("");
  const [statusFieldQuery, setStatusFieldQuery] = useState("");
  const [statusesToLoadQuery, setStatusesToLoadQuery] = useState("");
  const [doneStatusQuery, setDoneStatusQuery] = useState("");
  const [dueDateFieldQuery, setDueDateFieldQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      {!vm.isAuthorisedProfileReady && (
        <Text>{text.unauthorisedWarningText}</Text>
      )}

      {vm.isAuthorisedProfileReady && (
        <SettingsSectionCard
          sectionOffset={sectionOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.salesforce.sectionDataSource",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.dataToLoadLabelText}
            hint={text.dataToLoadHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedObjectDefinitionName === vm.noneOptionValue
                  ? undefined
                  : vm.selectedObjectDefinitionName
              }
              selectedLabel={
                vm.objectDefinitions.find(
                  (objectDefinition) =>
                    objectDefinition.name === vm.selectedObjectDefinitionName,
                )?.label
              }
              query={objectDefinitionQuery}
              placeholder={text.dataToLoadPlaceholderText}
              options={vm.objectDefinitions.map((objectDefinition) => ({
                id: objectDefinition.name,
                label: objectDefinition.label,
              }))}
              onQueryChange={setObjectDefinitionQuery}
              onSelect={vm.onSelectObjectDefinition}
              clearSelectionText={text.dataToLoadPlaceholderText}
              isLoading={!vm.objectDefinitionsLoaded}
            />
          </Field>
        </SettingsSectionCard>
      )}

      {vm.isAuthorisedProfileReady &&
        vm.selectedObjectDefinitionName !== vm.noneOptionValue && (
          <SettingsSectionCard
            sectionOffset={sectionOffset + 1}
            title={t(
              "board.connections.catalogue.sourceSettings.salesforce.sectionFilters",
            )}
          >
            <Field
              orientation="horizontal"
              label={text.statusFieldLabelText}
              hint={text.statusFieldHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedStatusFieldName === vm.noneOptionValue
                    ? undefined
                    : vm.selectedStatusFieldName
                }
                selectedLabel={
                  vm.picklistFields.find(
                    (picklistField) =>
                      picklistField.name === vm.selectedStatusFieldName,
                  )?.label
                }
                query={statusFieldQuery}
                placeholder={text.statusFieldPlaceholderText}
                options={vm.picklistFields.map((picklistField) => ({
                  id: picklistField.name,
                  label: picklistField.label,
                }))}
                onQueryChange={setStatusFieldQuery}
                onSelect={vm.onSelectStatusField}
                clearSelectionText={text.statusFieldPlaceholderText}
                isLoading={!vm.objectMetadataLoaded}
              />
              {vm.objectMetadataLoaded && vm.picklistFields.length === 0 && (
                <Text>{text.noStatusFieldsAvailableText}</Text>
              )}
            </Field>

            {vm.selectedStatusFieldName !== vm.noneOptionValue && (
              <>
                <Field
                  orientation="horizontal"
                  label={text.statusesToLoadLabelText}
                  hint={text.statusesToLoadHintText}
                >
                  <SourceMultiTagPicker
                    selectedIds={vm.selectedStatusesToLoad}
                    query={statusesToLoadQuery}
                    placeholder={text.statusesToLoadPlaceholderText}
                    options={vm.statuses.map((status) => ({
                      id: status.value,
                      label: status.label,
                    }))}
                    onQueryChange={setStatusesToLoadQuery}
                    onSelectionChange={vm.onSelectStatusesToLoad}
                    minQueryLength={0}
                    typeToSearchText={text.statusesToLoadHintText}
                    isLoading={!vm.objectMetadataLoaded}
                  />
                </Field>

                <Field
                  orientation="horizontal"
                  label={text.doneStatusLabelText}
                  hint={text.doneStatusHintText}
                >
                  <SourceSingleTagPicker
                    selectedId={
                      vm.selectedDoneStatusValue === vm.noneOptionValue
                        ? undefined
                        : vm.selectedDoneStatusValue
                    }
                    selectedLabel={
                      vm.statuses.find(
                        (status) => status.value === vm.selectedDoneStatusValue,
                      )?.label
                    }
                    query={doneStatusQuery}
                    placeholder={text.doneStatusPlaceholderText}
                    options={vm.statuses.map((status) => ({
                      id: status.value,
                      label: status.label,
                    }))}
                    onQueryChange={setDoneStatusQuery}
                    onSelect={vm.onSelectDoneStatus}
                    clearSelectionText={text.doneStatusPlaceholderText}
                    isLoading={!vm.objectMetadataLoaded}
                  />
                </Field>
              </>
            )}

            <Field
              orientation="horizontal"
              label={text.dueDateFieldLabelText}
              hint={text.dueDateFieldHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedDueDateFieldName === vm.noneOptionValue
                    ? undefined
                    : vm.selectedDueDateFieldName
                }
                selectedLabel={
                  vm.dateFields.find(
                    (dateField) =>
                      dateField.name === vm.selectedDueDateFieldName,
                  )?.label
                }
                query={dueDateFieldQuery}
                placeholder={text.dueDateFieldPlaceholderText}
                options={vm.dateFields.map((dateField) => ({
                  id: dateField.name,
                  label: dateField.label,
                }))}
                onQueryChange={setDueDateFieldQuery}
                onSelect={vm.onSelectDueDateField}
                clearSelectionText={text.dueDateFieldPlaceholderText}
                isLoading={!vm.objectMetadataLoaded}
              />
            </Field>

            <Field orientation="horizontal" label={text.assignedToMeLabelText}>
              <Checkbox checked disabled />
            </Field>

            {vm.hasError && <Text>{text.loadErrorText}</Text>}
          </SettingsSectionCard>
        )}

      {vm.selectedStatusesToLoad.length > 0 && (
        <StatusMappingSection
          sectionNumber={sectionOffset + 1}
          items={vm.statuses
            .filter((s) => vm.selectedStatusesToLoad.includes(s.value))
            .map((s) => ({ id: s.value, name: s.label }))}
          mappings={
            ((editingConnection.configuration as Record<string, unknown>)
              ?.statusMappings as SourceStatusMapping[] | undefined) ?? []
          }
          boardStatuses={boardStatuses}
          onChange={(mappings) =>
            onUpdate({
              configuration: {
                ...(editingConnection.configuration as Record<string, unknown>),
                statusMappings: mappings,
              },
            })
          }
        />
      )}
    </>
  );
};
